import { Outlet, useLocation } from 'react-router-dom';
import { MODULE_REGISTRY, type ModuleKey } from '@symphenia/shared-types';
import { useAuthStore } from '@/lib/auth.store';
import ModuleLockedPage from '@/components/ModuleLockedPage';

/**
 * AP-29-D — Ein Wächter für einen ganzen Pfad-Ast.
 *
 * Vorher hing `requiredModule` an genau **einer** von rund fünfzig Routen. Der
 * Grund, warum so eine Sperre erodiert, ist immer derselbe: Eine neue
 * Unterseite kommt dazu und niemand denkt an die Eigenschaft. Deshalb hängt das
 * Gate hier nicht an der einzelnen Route, sondern am **Pfad-Präfix** — alles
 * unter `/time-tracking/*` liegt in einem Gate, und eine neue Unterseite ist
 * automatisch mitgeschützt.
 *
 * Die Zuordnung kommt aus dem `area`-Feld der Registry; nur Pfade, die nicht
 * mit ihrem Bereich anfangen, stehen unten einzeln.
 *
 * **Das ist keine Sicherheitsgrenze.** Die liegt im `ModuleGuard` des Backends.
 * Hier geht es darum, dass niemand vor einer Seite steht, die leer bleibt oder
 * mit 403 antwortet — und dass er erfährt, an wen er sich wenden kann.
 */

/**
 * Alle Pfad-Äste der Registry, **längster zuerst**.
 *
 * Die Sortierung ist der ganze Trick (AP-29-N-D): `/time-tracking/qr-tags`
 * gehört dem Baustein `TIME_TRACKING.SCAN`, `/time-tracking/summary` der Gruppe
 * `TIME_TRACKING`. Gewinnt der längste Treffer, braucht es dafür keine
 * Ausnahmeliste — und ein neuer Baustein ordnet sich von selbst ein, sobald er
 * seinen Ast in der Registry nennt.
 *
 * Einträge ohne Ast fallen heraus: Ein quer schneidender Baustein (Projektzeit,
 * GPS) hat keine eigene Seite und wird an seiner Konfiguration gesperrt.
 */
const AESTE: { prefix: string; module: ModuleKey }[] = MODULE_REGISTRY.flatMap((m) =>
  (m.areas ?? []).map((area) => ({ prefix: `/${area}`, module: m.key })),
)
  .concat([
    { prefix: '/settings/ai', module: 'AI_ASSISTANT' },
    // Die QR-Verteilung für die Mitarbeiter-App gehört zur Zeiterfassung —
    // ohne das Modul gibt es nichts zu verteilen (AP-17).
    { prefix: '/mitarbeiter-app', module: 'TIME_TRACKING' },
  ])
  .sort((a, b) => b.prefix.length - a.prefix.length);

/**
 * Welches Modul verlangt dieser Pfad? `null` = keins (offen für jeden
 * angemeldeten Benutzer: Dashboard, Profil, Einstellungen, Benutzerverwaltung).
 */
export function moduleFuerPfad(pathname: string): ModuleKey | null {
  const treffer = AESTE.find(
    (a) => pathname === a.prefix || pathname.startsWith(`${a.prefix}/`),
  );
  return treffer?.module ?? null;
}

export function ModuleGate() {
  const { pathname } = useLocation();
  const hasModule = useAuthStore((s) => s.hasModule);

  const benoetigt = moduleFuerPfad(pathname);
  if (benoetigt && !hasModule(benoetigt)) {
    // Bewusst **keine** Umleitung auf `/`: Die hat bisher stillschweigend
    // geschluckt, warum die Seite nicht kam — der Benutzer hat dreimal geklickt
    // und dann angerufen.
    return <ModuleLockedPage moduleKey={benoetigt} />;
  }

  return <Outlet />;
}
